import { useState, useRef, useEffect, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CalendarBlank, Users, MagnifyingGlass, Minus, Plus } from '@phosphor-icons/react';
import type { SearchResultCard } from '@pms/shared';
import { LocationSearchInput, type LocationGroup, type LocationOption } from './LocationSearchInput';
import { useLocationAutocomplete } from '../../hooks/useLocationAutocomplete';
import { searchStays } from '../../services/api/search.api';

// Augment caps (D10): own "BestAcco stays" first, then geo "Places".
const OWN_RESULTS_CAP = 5;
const GEO_RESULTS_CAP = 6;

/**
 * Hero "explore" bar — Warm Editorial. Destination, check-in, check-out and a
 * guests stepper, then an Explore CTA. The /search param contract is preserved
 * verbatim (destination / checkIn / checkOut / guests). BestAcco is a directory,
 * so this carries intent into the results page — there is no booking here.
 */
export function SearchBar() {
  const navigate = useNavigate();
  const [destination, setDestination] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const guestsRef = useRef<HTMLDivElement>(null);

  // Geo "Places" source (proxy) + own "BestAcco stays" source (existing search).
  const geo = useLocationAutocomplete({ limit: GEO_RESULTS_CAP });
  const [ownResults, setOwnResults] = useState<SearchResultCard[]>([]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (guestsRef.current && !guestsRef.current.contains(e.target as Node)) {
        setGuestsOpen(false);
      }
    }
    if (guestsOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [guestsOpen]);

  // Own-results augment: debounced reuse of the existing marketplace search.
  useEffect(() => {
    const q = destination.trim();
    if (q.length < 2) {
      setOwnResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      searchStays({ destination: q, limit: OWN_RESULTS_CAP })
        .then((r) => {
          if (!cancelled) setOwnResults(r.data.slice(0, OWN_RESULTS_CAP));
        })
        .catch(() => {
          if (!cancelled) setOwnResults([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [destination]);

  function handleDestinationChange(text: string) {
    setDestination(text);
    geo.setQuery(text);
  }

  function handleLocationSelect(opt: LocationOption) {
    setDestination(opt.suggestion ? opt.suggestion.label : opt.label);
  }

  const ownItems: LocationOption[] = ownResults.map((r) => ({
    id: `own:${r.id}`,
    label: r.name,
    sublabel: r.location,
  }));
  const geoItems: LocationOption[] = geo.results.slice(0, GEO_RESULTS_CAP).map((s) => ({
    id: `geo:${s.id}`,
    label: s.label,
    suggestion: s,
  }));
  const locationGroups: LocationGroup[] = [
    ...(ownItems.length ? [{ label: 'BestAcco stays', items: ownItems }] : []),
    ...(geoItems.length ? [{ label: 'Places', items: geoItems }] : []),
  ];

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    const params = new URLSearchParams({
      ...(destination && { destination }),
      ...(checkIn && { checkIn }),
      ...(checkOut && { checkOut }),
      guests: String(guests),
    });
    navigate(`/search?${params.toString()}`);
  }

  return (
    <form
      onSubmit={handleSearch}
      className="flex flex-col gap-2 rounded-3xl border border-sand bg-white p-2 shadow-[0_24px_60px_-30px_rgba(28,26,23,0.55)] lg:flex-row lg:items-stretch lg:gap-0 lg:rounded-full lg:p-1.5 lg:pl-3"
    >
      <Segment icon={<MapPin className="h-5 w-5 text-clay" />} label="Where to" className="lg:flex-[1.4]">
        <LocationSearchInput
          value={destination}
          onChange={handleDestinationChange}
          onSelect={handleLocationSelect}
          groups={locationGroups}
          status={geo.status}
          placeholder="Search a city or hotel"
          ariaLabel="Where to"
          inputClassName="w-full bg-transparent text-ink placeholder-ink-muted/70 outline-none"
        />
      </Segment>

      <Divider />

      <Segment icon={<CalendarBlank className="h-5 w-5 text-clay" />} label="Check in">
        <input
          type="date"
          value={checkIn}
          onChange={(e) => setCheckIn(e.target.value)}
          aria-label="Check-in date"
          className="w-full bg-transparent text-ink outline-none [color-scheme:light]"
        />
      </Segment>

      <Divider />

      <Segment icon={<CalendarBlank className="h-5 w-5 text-clay" />} label="Check out">
        <input
          type="date"
          value={checkOut}
          min={checkIn || undefined}
          onChange={(e) => setCheckOut(e.target.value)}
          aria-label="Check-out date"
          className="w-full bg-transparent text-ink outline-none [color-scheme:light]"
        />
      </Segment>

      <Divider />

      {/* Guests stepper */}
      <div ref={guestsRef} className="relative flex items-center lg:flex-1">
        <button
          type="button"
          onClick={() => setGuestsOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={guestsOpen}
          className="flex w-full items-center gap-2.5 rounded-2xl px-4 py-2 text-left transition hover:bg-cream lg:rounded-full"
        >
          <Users className="h-5 w-5 shrink-0 text-clay" aria-hidden="true" />
          <span className="flex min-w-0 flex-col">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Guests</span>
            <span className="text-ink">{guests} {guests === 1 ? 'guest' : 'guests'}</span>
          </span>
        </button>

        {guestsOpen && (
          <div
            role="dialog"
            aria-label="Select guests"
            className="absolute left-0 top-full z-20 mt-3 w-64 max-w-[calc(100vw-3rem)] rounded-2xl border border-sand bg-white p-4 shadow-[0_24px_60px_-30px_rgba(28,26,23,0.6)]"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-ink">Guests</p>
                <p className="text-sm text-ink-muted">How many travelling?</p>
              </div>
              <div className="flex items-center gap-3">
                <Stepper
                  label="Decrease guests"
                  onClick={() => setGuests((g) => Math.max(1, g - 1))}
                  disabled={guests <= 1}
                  icon={<Minus className="h-4 w-4" />}
                />
                <span className="w-5 text-center font-semibold text-ink" aria-live="polite">{guests}</span>
                <Stepper
                  label="Increase guests"
                  onClick={() => setGuests((g) => Math.min(20, g + 1))}
                  disabled={guests >= 20}
                  icon={<Plus className="h-4 w-4" />}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <button
        type="submit"
        className="flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-clay px-7 py-3 font-semibold text-white transition hover:bg-clay-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-white lg:rounded-full"
      >
        <MagnifyingGlass className="h-5 w-5" aria-hidden="true" />
        <span>Explore</span>
      </button>
    </form>
  );
}

function Segment({
  icon,
  label,
  children,
  className = '',
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex items-center gap-2.5 rounded-2xl px-4 py-2 transition hover:bg-cream lg:rounded-full ${className}`}>
      {icon}
      <span className="flex min-w-0 flex-col">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
        {children}
      </span>
    </label>
  );
}

function Divider() {
  return <span className="mx-1 hidden w-px self-center bg-sand lg:block lg:h-9" aria-hidden="true" />;
}

function Stepper({
  label,
  onClick,
  disabled,
  icon,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex h-8 w-8 items-center justify-center rounded-full border border-sand text-ink transition hover:border-clay hover:text-clay disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay"
    >
      {icon}
    </button>
  );
}
